import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Password } from 'primereact/password';

interface PasswordStepProps {
  mode: 'auto' | 'manual';
  password: string;
  onChange: (mode: 'auto' | 'manual', password: string) => void;
  onBack: () => void;
  onNext: () => void;
}

export const PasswordStep: React.FC<PasswordStepProps> = ({
  mode,
  password,
  onChange,
  onBack,
  onNext,
}) => {
  const [confirm, setConfirm] = useState(mode === 'manual' ? password : '');

  const tooShort = mode === 'manual' && password.length < 12;
  const mismatch = mode === 'manual' && password !== confirm;
  const canProceed = mode === 'auto' || (!tooShort && !mismatch);

  return (
    <div data-component="ldap-wizard-password" className="flex flex-col gap-4">
      <div
        className={`border rounded-lg p-3 cursor-pointer ${
          mode === 'auto' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}
        onClick={() => onChange('auto', '')}
      >
        <div className="flex items-center gap-3">
          <RadioButton checked={mode === 'auto'} readOnly />
          <div>
            <div className="font-medium">Generate a strong password (recommended)</div>
            <div className="text-xs text-gray-500">
              A random 20-character password is generated and shown once after creation.
              The user can change it later at pass.linienriss.org.
            </div>
          </div>
        </div>
      </div>

      <div
        className={`border rounded-lg p-3 cursor-pointer ${
          mode === 'manual' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}
        onClick={() => mode !== 'manual' && onChange('manual', password)}
      >
        <div className="flex items-center gap-3">
          <RadioButton checked={mode === 'manual'} readOnly />
          <div className="font-medium">Set a password manually</div>
        </div>
        {mode === 'manual' && (
          <div className="mt-3 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <label className="text-sm font-medium block mb-1">Password *</label>
              <Password
                value={password}
                onChange={(e) => onChange('manual', e.target.value)}
                toggleMask
                feedback
                className="w-full"
                inputClassName={`w-full ${tooShort && password ? 'p-invalid' : ''}`}
              />
              {tooShort && password && (
                <small className="text-red-500 mt-1 block">At least 12 characters required</small>
              )}
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Confirm Password *</label>
              <Password
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                toggleMask
                feedback={false}
                className="w-full"
                inputClassName={`w-full ${mismatch && confirm ? 'p-invalid' : ''}`}
              />
              {mismatch && confirm && (
                <small className="text-red-500 mt-1 block">Passwords do not match</small>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-2">
        <Button label="Back" icon="pi pi-arrow-left" className="p-button-text" onClick={onBack} />
        <Button
          label="Next"
          icon="pi pi-arrow-right"
          iconPos="right"
          disabled={!canProceed}
          onClick={onNext}
        />
      </div>
    </div>
  );
};
